import { useState, useEffect } from 'react';
import { Button } from '@mantine/core';
import { useTranslation } from '../context/TranslationContext';
import businessUpgradeService from '../services/business_upgrade';
import './AdminProducts.css';

function formatDateTime(str) {
  if (!str) return '';
  const d = new Date(str);
  return d.toLocaleDateString('en-CH', { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
}

const STATUS_TABS = ['pending', 'approved', 'rejected'];

export default function AdminBusinessRequests() {
  const { t } = useTranslation();
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('pending');
  const [actionLoading, setActionLoading] = useState(null);
  const [rejectReason, setRejectReason] = useState({});
  const [showRejectInput, setShowRejectInput] = useState(null);

  const fetchRequests = async (status) => {
    try {
      setLoading(true);
      const data = await businessUpgradeService.getAllRequests(status);
      setRequests(data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRequests(filter);
  }, [filter]);

  const handleApprove = async (id) => {
    try {
      setActionLoading(id);
      await businessUpgradeService.approveRequest(id);
      setRequests(prev => prev.filter(r => r.id !== id));
    } catch (err) {
      console.error(err);
    } finally {
      setActionLoading(null);
    }
  };

  const handleReject = async (id) => {
    try {
      setActionLoading(id);
      await businessUpgradeService.rejectRequest(id, rejectReason[id] || '');
      setRequests(prev => prev.filter(r => r.id !== id));
      setRejectReason(prev => { const n = { ...prev }; delete n[id]; return n; });
      setShowRejectInput(null);
    } catch (err) {
      console.error(err);
    } finally {
      setActionLoading(null);
    }
  };

  return (
    <div className="admin-products dashboard-view-wrapper">
      <header className="dashboard-page-header">
        <div>
          <h1 className="dashboard-page-title">{t('businessUpgradeRequests', 'Business Upgrade Requests')}</h1>
          <p className="dashboard-page-description">{t('businessUpgradeRequestsDesc', 'Review and manage customer requests to upgrade to business accounts.')}</p>
        </div>
      </header>

      <div className="settings-tabs" role="group" aria-label={t('businessRequestStatus', 'Filter by request status')} style={{ display: 'flex', gap: '0.5rem', margin: '1rem 0', flexWrap: 'wrap' }}>
        {STATUS_TABS.map(s => (
          <Button
            key={s}
            variant={filter === s ? 'filled' : 'subtle'}
            color={filter === s ? 'blue' : 'gray'}
            onClick={() => setFilter(s)}
            size="sm"
            aria-pressed={filter === s}
            aria-label={`${t(`status_${s}`, s.charAt(0).toUpperCase() + s.slice(1))} requests tab`}
          >
            {t(`status_${s}`, s.charAt(0).toUpperCase() + s.slice(1))}
            {s === 'pending' && filter !== s && requests.length > 0 && ` (${requests.length})`}
          </Button>
        ))}
      </div>

      {loading ? (
        <p role="status" aria-live="polite">{t('loading', 'Loading…')}</p>
      ) : (
        <div className="table-responsive">
          <table className="admin-table">
            <caption className="sr-only">{t('businessRequestsCaption', 'Business upgrade requests filtered by status, with customer info and admin actions')}</caption>
            <thead>
              <tr>
                <th scope="col">{t('customer', 'Customer')}</th>
                <th scope="col">{t('email', 'Email')}</th>
                <th scope="col">{t('requestedOn', 'Requested On')}</th>
                {filter === 'rejected' && <th scope="col">{t('reason', 'Reason')}</th>}
                {filter === 'pending' && <th scope="col">{t('actions', 'Actions')}</th>}
              </tr>
            </thead>
            <tbody>
              {requests.map(r => (
                <tr key={r.id}>
                  <td>{r.user_name}</td>
                  <td>{r.user_email}</td>
                  <td>{formatDateTime(r.created_at)}</td>
                  {filter === 'rejected' && (
                    <td style={{ color: '#6b7280', fontSize: '0.875rem' }}>
                      {r.rejection_reason || '—'}
                    </td>
                  )}
                  {filter === 'pending' && (
                    <td style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', flexWrap: 'wrap' }}>
                      <Button
                        size="xs"
                        color="green"
                        loading={actionLoading === r.id}
                        onClick={() => handleApprove(r.id)}
                        disabled={actionLoading !== null}
                      >
                        {t('approve', 'Approve')}
                      </Button>
                      {showRejectInput === r.id ? (
                        <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                          <input
                            type="text"
                            id={`reject-reason-${r.id}`}
                            placeholder={t('rejectionReasonOptional', 'Reason (optional)')}
                            value={rejectReason[r.id] || ''}
                            onChange={e => setRejectReason(prev => ({ ...prev, [r.id]: e.target.value }))}
                            style={{ padding: '0.35rem 0.6rem', border: '1px solid #d1d5db', borderRadius: 6, fontSize: '0.85rem', minWidth: 160 }}
                            aria-label={`${t('rejectionReason', 'Rejection reason')} for ${r.user_name}`}
                          />
                          <Button
                            size="xs"
                            color="red"
                            loading={actionLoading === r.id}
                            onClick={() => handleReject(r.id)}
                            disabled={actionLoading !== null}
                          >
                            {t('confirmReject', 'Confirm')}
                          </Button>
                          <Button
                            size="xs"
                            variant="subtle"
                            color="gray"
                            onClick={() => { setShowRejectInput(null); setRejectReason(prev => { const n = { ...prev }; delete n[r.id]; return n; }); }}
                            disabled={actionLoading !== null}
                          >
                            {t('cancel', 'Cancel')}
                          </Button>
                        </div>
                      ) : (
                        <Button
                          size="xs"
                          color="red"
                          variant="light"
                          onClick={() => setShowRejectInput(r.id)}
                          disabled={actionLoading !== null}
                        >
                          {t('reject', 'Reject')}
                        </Button>
                      )}
                    </td>
                  )}
                </tr>
              ))}
              {requests.length === 0 && (
                <tr>
                  <td colSpan={filter === 'rejected' ? 4 : filter === 'pending' ? 4 : 3}>
                    {t('noRequestsFound', 'No requests found.')}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
